import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { captureUtm, getUtm } from "../lib/utm";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CheckCircle, Phone, ArrowRight, ShieldCheck, CaretDown, XCircle, HandHeart, ForkKnife, Car, Sparkle, UsersThree, Heart } from "@phosphor-icons/react";

const PHONE = "800-719-6912";
const PHONE_TEL = "tel:8007196912";

const NOISE = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E")`;
const Noise = () => <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: NOISE }} />;

const TASKS = [
  { icon: HandHeart, t: "Personal care", d: "Bathing, grooming, dressing, moving safely through the day" },
  { icon: ForkKnife, t: "Meals & nutrition", d: "Meals cooked in their kitchen, the way they like them" },
  { icon: Sparkle, t: "Housekeeping & laundry", d: "A clean home, fresh linens, no lifting" },
  { icon: Car, t: "Rides & errands", d: "Doctor's appointments, pharmacy, grocery runs" },
  { icon: UsersThree, t: "Companionship", d: "Conversation, cards, walks — someone reliable in their corner" },
  { icon: Heart, t: "Respite for the rest of the family", d: "A real break for everyone else carrying it" },
];

const faqsFor = () => [
  ["Is this a real program?", "Yes. It's Pennsylvania's Community HealthChoices (CHC) program — Medicaid's managed-care program for long-term care. Within it, a participant-directed option (called Services My Way) lets the person receiving care choose their caregiver — including certain family members and friends — and the program pays for the approved hours."],
  ["Who can get paid to be a caregiver?", "Adult children, siblings, other relatives, friends, and neighbors can often be paid — as long as they're 18 or older, legally able to work in the U.S., and pass the required clearances. The person receiving care must qualify for CHC (PA Medicaid, 21+, and a nursing-facility level of need)."],
  ["Who can NOT be paid?", "Under CHC's participant-directed option, spouses can't be paid — and neither can legal guardians, representative payees, or anyone holding power of attorney for the person. If that's you, the program can still pay for a professional caregiver, and we'll help you set that up instead."],
  ["How much do family caregivers get paid in Pennsylvania?", "Caregivers are paid hourly for the hours the program approves in the care plan — the rate and hours depend on the plan, the county, and the level of need. We give you the real numbers for your situation in the free review, before you commit to anything."],
  ["Is the pay really tax-free?", "It can be, for live-in caregivers. IRS Notice 2014-7 lets qualifying Medicaid waiver payments be excluded from federal income tax when the caregiver lives in the same home as the person receiving care — related or not. See the IRS guidance ('Certain Medicaid waiver payments may be excludable from income') and confirm your situation with a tax professional."],
  ["What if my loved one doesn't have Medicaid yet?", "They may still qualify. The application goes through PA's COMPASS system, followed by a needs assessment through the PA Independent Enrollment Broker (1-877-550-4227). We'll point you in the right direction either way — the review is free."],
  ["How long does it take?", "It depends on where you're starting. If your loved one already has CHC, the caregiver enrollment is mostly clearances and onboarding — and we walk you through it. If Medicaid itself still needs to be set up, that part takes longer. We'll tell you honestly where you stand after the review."],
  ["Why do this through Silver Home Care?", "We're a local, licensed agency — ten years serving Bucks and Philadelphia. We walk you through enrollment and clearances step by step, and when you call, a person answers. You're not figuring this out alone."],
];

export default function PaidCaregiverPage({ county = "Pennsylvania", slug = "pennsylvania" }) {
  const [lead, setLead] = useState({ first: "", last: "", phone: "", email: "", who: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const FAQS = faqsFor();
  const leadValid = lead.first.trim() && lead.last.trim() && (lead.phone.trim() || /.+@.+\..+/.test(lead.email));

  useEffect(() => {
    captureUtm();
    document.title = `Get Paid to Care for a Loved One in ${county} | Silver Home Care`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = `Pennsylvania's Community HealthChoices program can pay family members and friends for the care they already give. Who qualifies, who can be paid, how it works — free 60-second eligibility check from a local ${county} agency.`;
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "faq-schema";
    ld.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) });
    document.head.appendChild(ld);
    return () => { document.getElementById("faq-schema")?.remove(); };
  }, [county]); // eslint-disable-next-line

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/care-inquiry", { name: `${lead.first.trim()} ${lead.last.trim()}`, phone: lead.phone, email: lead.email,
        answers: { who: lead.who }, utm: getUtm(), source: `paid-caregiver-${slug}` });
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const toForm = () => document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  const cta = (testid, label = "Check my eligibility — free") => (
    <button onClick={toForm}
      className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-8 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-1 hover:bg-[#4A235A]"
      data-testid={testid}>
      {label} <ArrowRight size={18} weight="bold" />
    </button>
  );

  const leadCard = (
    <div id="lead-form" className="rounded-2xl bg-white p-6 shadow-2xl shadow-black/30 sm:p-10" data-testid="lead-card">
      {sent ? (
        <div className="text-center" data-testid="inquiry-success">
          <CheckCircle size={56} weight="fill" className="mx-auto text-[#6A3E97]" />
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-[#23282f]">You're all set{lead.first ? `, ${lead.first}` : ""}.</h2>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#23282f]/70 sm:text-base">
            A care coordinator will review your situation and call you — usually the same or next business day.
            Want to talk now? <a href={PHONE_TEL} className="font-bold text-[#6A3E97]">{PHONE}</a>
          </p>
        </div>
      ) : (
        <>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-[#23282f] sm:text-3xl" data-testid="lead-headline">Find out what you may be eligible to earn</h2>
          <p className="mt-2 text-sm leading-relaxed text-[#23282f]/60">Free review by a real person — not a computer. No obligation, no cost.</p>
          <div className="mt-6 space-y-4">
            <select value={lead.who} onChange={(e) => setLead((l) => ({ ...l, who: e.target.value }))}
              className="h-14 w-full rounded-md border border-[#23282f]/15 bg-white px-3 text-base text-[#23282f]/80" data-testid="lead-who">
              <option value="">Who are you caring for? (optional)</option>
              <option>My parent</option>
              <option>Another family member</option>
              <option>A friend or neighbor</option>
              <option>My spouse — I know the rules are different</option>
              <option>Just looking into it</option>
            </select>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input value={lead.first} onChange={(e) => setLead((l) => ({ ...l, first: e.target.value }))} placeholder="First name *"
                className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-name" />
              <Input value={lead.last} onChange={(e) => setLead((l) => ({ ...l, last: e.target.value }))} placeholder="Last name *"
                className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-last-name" />
            </div>
            <Input value={lead.phone} onChange={(e) => setLead((l) => ({ ...l, phone: e.target.value }))} placeholder="Mobile phone number"
              type="tel" className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-phone" />
            <Input type="email" value={lead.email} onChange={(e) => setLead((l) => ({ ...l, email: e.target.value }))} placeholder="Email address"
              className="h-14 border-[#23282f]/15 bg-white text-base focus:ring-2 focus:ring-[#6A3E97]" data-testid="lead-email" />
            <Button onClick={submit} disabled={busy || !leadValid} size="lg"
              className="w-full rounded-full bg-[#6A3E97] py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#4A235A] active:translate-y-0"
              data-testid="lead-submit">
              {busy ? "Sending…" : "Get my free review"} <ArrowRight size={18} weight="bold" className="ml-1" />
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-center text-xs text-[#23282f]/45">
              <ShieldCheck size={13} /> Private & secure · a real person responds within one business day
            </p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-white text-[#23282f]" data-testid="paid-caregiver-page">
      <style>{`
        @keyframes fcRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: translateY(0); } }
        .fc-rise { animation: fcRise .7s cubic-bezier(.22,.68,.36,1) both; }
        .fc-rise-1 { animation-delay: .08s; } .fc-rise-2 { animation-delay: .18s; } .fc-rise-3 { animation-delay: .28s; }
      `}</style>

      <header className="sticky top-0 z-50 border-b border-[#23282f]/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
          <a href="/" className="flex items-center gap-2.5">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
            <p className="font-heading text-lg font-semibold tracking-tight">Silver Home Care</p>
          </a>
          <nav className="hidden items-center gap-6 text-sm font-medium text-stone-600 md:flex" data-testid="page-nav">
            <a href="/#services" className="transition-colors hover:text-[#6A3E97]">Services</a>
            <a href="/#about" className="transition-colors hover:text-[#6A3E97]">About us</a>
            <a href="/reviews" className="transition-colors hover:text-[#6A3E97]">Reviews</a>
            <a href="/free-care" className="font-semibold text-[#6A3E97] transition-colors hover:text-[#55317A]">Free services</a>
            <a href="/apply" className="transition-colors hover:text-[#6A3E97]">Apply</a>
            <a href="/#contact" className="transition-colors hover:text-[#6A3E97]">Contact</a>
          </nav>
          <a href={PHONE_TEL}
            className="inline-flex items-center gap-2 rounded-full bg-[#6A3E97] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#4A235A] focus:ring-2 focus:ring-[#6A3E97] focus:ring-offset-2"
            data-testid="header-phone">
            <Phone size={16} weight="fill" /> {PHONE}
          </a>
        </div>
      </header>

      {/* HERO — plum split, lead card on the right */}
      <section className="relative overflow-hidden bg-[#4A235A] text-white">
        <Noise />
        <div className="relative mx-auto grid max-w-7xl items-start gap-10 px-5 pb-16 pt-14 sm:px-8 lg:grid-cols-2 lg:pb-24 lg:pt-20">
          <div>
            <p className="fc-rise text-xs font-bold uppercase tracking-[0.2em] text-[#C9A6E8] sm:text-sm">{county} · Community HealthChoices</p>
            <h1 className="fc-rise fc-rise-1 mt-5 font-heading text-5xl font-bold leading-[1.02] tracking-tight sm:text-6xl" data-testid="hero-headline">
              You may be <span className="text-[#C9A6E8]">eligible to get paid</span> to care for someone you love
            </h1>
            <p className="fc-rise fc-rise-2 mt-7 max-w-lg text-base leading-relaxed text-white/85 sm:text-lg">
              If you're already looking after a parent, relative, or friend — the meals, the bathing, the rides,
              keeping them safe at home — Pennsylvania's Community HealthChoices program may be able to
              <strong className="font-bold text-white"> pay you for the care you already give</strong>.
            </p>
            <ul className="fc-rise fc-rise-3 mt-8 space-y-3">
              {["Hourly pay for the care you're already giving",
                "We walk you through enrollment and clearances, step by step",
                "A local office that answers when you call"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-white/90 sm:text-base">
                  <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-[#C9A6E8]" /> {t}
                </li>
              ))}
            </ul>
            <div className="fc-rise fc-rise-3 mt-9 hidden overflow-hidden rounded-2xl shadow-lg lg:block">
              <img src="/shc-gen-family-welcome.webp" alt="A Silver Home Care caregiver welcomed at the door by a client's family" className="w-full object-cover" data-testid="hero-image" />
            </div>
          </div>
          <div>
            {leadCard}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs font-bold uppercase tracking-[0.15em] text-white/60" data-testid="trust-strip">
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#C9A6E8]" /> Licensed PA agency</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#C9A6E8]" /> Ten years of service</span>
              <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#C9A6E8]" /> Free review</span>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS IN PA */}
      <section className="px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6A3E97]">The real path</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="path-heading">How it actually works in Pennsylvania</h2>
          <p className="mt-4 text-base leading-relaxed text-[#23282f]/70 sm:text-lg">The program is real, and the path is specific. Here's the road, step by step:</p>
          <ol className="mt-10 space-y-5">
            {[
              { t: "Your loved one needs PA Medicaid", d: "An ACCESS card. If they don't have it yet, the application goes through PA's COMPASS system — we'll point you to it." },
              { t: "They get a needs assessment", d: "Through the PA Independent Enrollment Broker (1-877-550-4227). This confirms they need nursing-facility-level help with daily life — bathing, meals, getting around." },
              { t: "They enroll in Community HealthChoices", d: "CHC is Pennsylvania's Medicaid managed-care program for long-term care at home. They pick a plan — and ask for the participant-directed option, called Services My Way." },
              { t: "You enroll as their caregiver — and get paid", d: "Clearances, employment paperwork, onboarding. Then you're paid hourly for the approved hours of care you provide." },
            ].map((s, i) => (
              <li key={s.t} className="flex items-start gap-5 rounded-2xl border border-[#23282f]/10 bg-white p-6 shadow-[2px_2px_10px_rgba(0,0,0,0.05)]" data-testid={`path-step-${i + 1}`}>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#6A3E97] font-heading text-lg font-bold text-white">{i + 1}</span>
                <div>
                  <p className="font-heading text-xl font-semibold tracking-tight">{s.t}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-[#23282f]/65">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-10">{cta("path-cta", "See if we qualify — 60 seconds")}</div>
        </div>
      </section>

      {/* WHO CAN / CAN'T */}
      <section className="bg-[#C9A6E8]/15 px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="who-heading">Who can — and can't — be paid</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-[#27AE60]/30 bg-white p-8" data-testid="who-can">
              <p className="font-heading text-2xl font-semibold tracking-tight">Usually yes</p>
              <ul className="mt-5 space-y-3.5">
                {["Adult children caring for a parent", "Siblings, grandchildren, other relatives", "Friends and neighbors", "Anyone 18+, work-authorized, who passes clearances"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-base text-[#23282f]/80"><CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#1e8449]" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-[#B7791F]/30 bg-white p-8" data-testid="who-cant">
              <p className="font-heading text-2xl font-semibold tracking-tight">Not through this program</p>
              <ul className="mt-5 space-y-3.5">
                {["Spouses", "Legal guardians", "Representative payees", "Anyone holding power of attorney"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-base text-[#23282f]/80"><XCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-[#8a5d14]" /> {t}</li>
                ))}
              </ul>
              <p className="mt-5 rounded-xl bg-[#B7791F]/5 p-4 text-sm leading-relaxed text-[#23282f]/70">
                Caring for a spouse? You can't be paid through this option — but the program can still pay for a professional caregiver,
                and we'll help you set that up. Same free review.
              </p>
            </div>
          </div>
          <div className="mt-10">{cta("who-cta", "Ask about my situation")}</div>
        </div>
      </section>

      {/* THE TROJAN HORSE — tax exclusion, dark band */}
      <section className="relative overflow-hidden bg-[#23282f] px-5 py-16 text-white sm:px-8 lg:py-24">
        <Noise />
        <div className="relative mx-auto max-w-3xl text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C9A6E8]">The part most people never hear about</p>
          <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="tax-heading">
            Live with the person you care for? Your pay may be <span className="text-[#C9A6E8]">tax-free</span>.
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            Under IRS Notice 2014-7, qualifying Medicaid waiver payments count as "difficulty of care" payments —
            and when the caregiver lives in the same home as the person they care for, that pay can be
            <strong className="font-bold text-white"> excluded from federal income tax</strong>. Related or not.
            So the money you earn caring for your mom in the home you share may be yours to keep — untaxed.
          </p>
          <a href="https://www.irs.gov/individuals/certain-medicaid-waiver-payments-may-be-excludable-from-income"
            target="_blank" rel="noreferrer"
            className="mt-7 inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-7 py-3 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-1 hover:bg-white hover:text-[#23282f]"
            data-testid="tax-irs-link">
            Read the IRS guidance <ArrowRight size={15} weight="bold" />
          </a>
          <p className="mx-auto mt-5 max-w-xl text-xs leading-relaxed text-white/50">
            We're not tax advisors and every situation is different — your tax professional can confirm how this applies to you.
            But most families we talk to have never been told this exists.
          </p>
          <div className="mt-8">{cta("tax-cta", "Find out if this fits my situation")}</div>
        </div>
      </section>

      {/* WHAT YOU'D BE PAID TO DO */}
      <section className="px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6A3E97]">The work itself</p>
          <h2 className="mt-3 font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="tasks-heading">What you'd be paid to do</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#23282f]/70 sm:text-lg">
            The everyday things that keep someone independent in their own home — the things you're probably already doing for free.
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TASKS.map((c) => (
              <div key={c.t} className="rounded-2xl border border-[#6A3E97]/15 bg-[#C9A6E8]/10 p-6 transition-transform duration-200 hover:-translate-y-1" data-testid={`task-${c.t.toLowerCase().replace(/\W+/g, "-")}`}>
                <c.icon size={28} weight="duotone" className="text-[#6A3E97]" />
                <p className="mt-3 font-heading text-lg font-semibold tracking-tight">{c.t}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-[#23282f]/60">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">{cta("tasks-cta", "Get my free review")}</div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-heading text-4xl font-semibold tracking-tight sm:text-5xl" data-testid="faq-heading">Common questions</h2>
          <div className="mt-10 divide-y divide-[#23282f]/10 border-y border-[#23282f]/10">
            {FAQS.map(([q, a], i) => (
              <div key={q}>
                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="flex w-full items-center justify-between py-5 text-left font-heading text-lg font-semibold text-[#23282f] transition-colors hover:text-[#6A3E97] focus:ring-2 focus:ring-[#6A3E97]"
                  data-testid={`faq-q-${i}`}>
                  {q}
                  <CaretDown size={18} className={`shrink-0 text-[#23282f]/40 transition-transform duration-200 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && <p className="pb-6 text-sm leading-relaxed text-[#23282f]/70 sm:text-base" data-testid={`faq-a-${i}`}>{a}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL BAND */}
      <section className="relative overflow-hidden bg-[#4A235A] px-5 py-16 text-white sm:px-8 lg:py-20">
        <Noise />
        <div className="relative mx-auto max-w-3xl text-center">
          <h2 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="final-heading">Wondering what your situation is worth?</h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
            The review is free — or call {PHONE} and ask a person.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            {cta("bottom-cta", "Find out what I may be eligible to earn")}
            <a href={PHONE_TEL}
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-8 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-1 hover:bg-white hover:text-[#4A235A]"
              data-testid="final-call">
              <Phone size={17} weight="fill" /> Call {PHONE}
            </a>
          </div>
        </div>
      </section>

      <footer className="bg-[#23282f] py-8 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 text-xs text-white/50 sm:px-8">
          <p>© {new Date().getFullYear()} Silver Home Care · Licensed Pennsylvania non-medical home care agency · {PHONE}</p>
          <div className="flex items-center gap-5">
            <a href="/" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-main-site">Main site</a>
            <a href="/free-care" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-free-care">Free care through CHC</a>
            <a href="/apply" className="font-medium text-white/60 transition-colors hover:text-white" data-testid="footer-careers">Work for us</a>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      {!sent && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-[#23282f]/10 bg-white/95 p-4 backdrop-blur-md lg:hidden" data-testid="sticky-mobile-cta">
          <div className="flex gap-2">
            <button onClick={toForm} className="flex-1 rounded-full bg-[#6A3E97] py-3.5 text-sm font-bold text-white shadow-md" data-testid="sticky-cta-btn">
              Get my free review
            </button>
            <a href={PHONE_TEL} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border-2 border-[#6A3E97] py-3.5 text-sm font-bold text-[#6A3E97]" data-testid="sticky-call-btn">
              <Phone size={15} weight="fill" /> Call now
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
